<script setup lang="ts">
import { normalizeCandidateReportCachePath } from "#bb/lib/pathContract";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field } = useInlineBlockEditor(props.id);
const route = useRoute();
const injectedReportUserId = inject<{ value?: string } | null>("reportUserId", null);

const reportPath = field<string>("reportPath", "");
const alternatePath = field<string>("alternatePath", "");
const checkReportData = field<boolean>("checkReportData", false);
const includeUserId = field<boolean>("includeUserId", false);
const reportFileName = field<string>("reportFileName", "report");
const delaySeconds = field<number>("delaySeconds", 0);
const editorCandidateId = computed(() =>
  injectedReportUserId?.value
  ?? (typeof route.query.user === "string" ? route.query.user : null),
);
const reportCachePath = computed({
  get: () => normalizeCandidateReportCachePath(reportFileName.value),
  set: (value: string) => {
    reportFileName.value = value;
  },
});
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:box-select" accent="navigation">
    <EditorField :label="t('targetLabel')" :hint="t('targetHint')">
      <EditorPathInput
        v-model="reportPath"
        mode="existing-file"
        :scopes="['site', 'external']"
        allow-http-urls
        :placeholder="t('targetPlaceholder')"
        aria-label="Primary redirect path"
      />
    </EditorField>

    <EditorField :label="t('checkReportDataLabel')" :hint="t('checkReportDataHint')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t('enabled') }}</span>
        <Switch v-model="checkReportData" />
      </div>
    </EditorField>

    <EditorField :label="t('includeUserIdLabel')" :hint="t('includeUserIdHint')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t('enabled') }}</span>
        <Switch v-model="includeUserId" />
      </div>
    </EditorField>

    <template v-if="checkReportData">
      <EditorField :label="t('alternateTargetLabel')" :hint="t('alternateTargetHint')">
        <EditorPathInput
          v-model="alternatePath"
          mode="existing-file"
          :scopes="['site', 'external']"
          allow-http-urls
          :placeholder="t('alternateTargetPlaceholder')"
          aria-label="Alternative redirect path"
        />
      </EditorField>

      <EditorField :label="t('reportFileNameLabel')" :hint="t('reportFileNameHint')">
        <EditorPathInput
          v-model="reportCachePath"
          mode="report-cache"
          :scopes="['user']"
          default-scope="user"
          :user-id="editorCandidateId"
          placeholder="/data/<userId>/cache/report"
          aria-label="Report cache path"
        />
      </EditorField>
    </template>

    <EditorField :label="t('delayLabel')">
      <Input
        v-model.number="delaySeconds"
        type="number"
        min="0"
        class="h-8 rounded-sm bg-muted text-[13px]"
      />
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Redirect"
  enabled: "Enabled"
  targetLabel: "Primary Redirect URL"
  targetPlaceholder: "/reports/me or https://example.com"
  targetHint: "Used as the default redirect. Internal paths and full http/https URLs are both supported."
  checkReportDataLabel: "Redirect based on report data"
  checkReportDataHint: "Check whether the current user has a cached report file before choosing the destination."
  includeUserIdLabel: "Include user id"
  includeUserIdHint: "Append the current user id as the user query parameter on whichever redirect URL is used."
  alternateTargetLabel: "Alternative Redirect URL"
  alternateTargetPlaceholder: "/assessment/start or https://example.com/start"
  alternateTargetHint: "Used when report-data checking is enabled and the configured report file does not exist for the current user."
  reportFileNameLabel: "Report Cache Path"
  reportFileNameHint: "Checks the configured candidate cache path. Legacy filename-only values remain supported."
  delayLabel: "Delay (seconds)"
</i18n>
